import { XMarkIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon, ChevronDoubleRightIcon, PlusIcon, DocumentIcon } from "@heroicons/react/24/outline";
import { Layout, showPopupMenu, TabNode, type IIcons, type NodeMouseEvent } from "flexlayout-react";
import "flexlayout-react/style/light.css";
import { useCallback, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import type { RouteObject } from "react-router";
import type { WorkspaceController } from "../workspaceController";
import { ROUTE_PATTERNS } from "../../../shared/routing/appPaths";
import IconButton from "../../../ui/buttons/IconButton";
import AnchoredTooltip from "../../../ui/overlays/AnchoredTooltip";
import { useAnchoredTooltip } from "../../../ui/overlays/useAnchoredTooltip";
import { APP_SECTIONS, sectionOfPath } from "../../appSections";
import { pathnameOf } from "../workspaceAddress";
import { tabPlace } from "./tabLayout";
import { layoutString, PAGE_LABELS } from "./tabLabels";
import TabHost from "./TabHost";

const ICONS: IIcons = {
  close: <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />,
  more: <ChevronDoubleRightIcon className="h-4 w-4" aria-hidden="true" />,
};

interface TabLayoutViewProps {
  readonly controller: WorkspaceController;
  readonly tabRoutes: readonly RouteObject[];
}

/**
 * The tab groups on screen (F90): FlexLayout draws the tab strips, drop zones, dividers and
 * the keyboard and ARIA behaviour; each tab's content is a `TabHost`. Styled with DMW's
 * tokens (theme/base/workspace-tabs.css).
 */
export default function TabLayoutView({ controller, tabRoutes }: TabLayoutViewProps) {
  const { t } = useTranslation();
  const { recentlyClosed } = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const tooltip = useAnchoredTooltip<string>();
  const factory = useCallback(
    (node: TabNode) => {
      const place = tabPlace(node);
      return place === null ? null : <TabHost controller={controller} tabId={node.getId()} route={place.route} tabRoutes={tabRoutes} />;
    },
    [controller, tabRoutes],
  );
  const translate = useCallback((key: string) => layoutString(key, t), [t]);
  // The tab menu, on a right click or Shift+F10 on a tab (Design B, §6).
  const showTabMenu = useCallback<NodeMouseEvent>(
    (node, event) => {
      if (!(node instanceof TabNode)) return;
      event.preventDefault();
      const id = node.getId();
      // Inside the layout, so the menu takes the layout's theme.
      const layout = event.currentTarget.closest<HTMLElement>(".flexlayout__layout");
      showPopupMenu({
        anchor: { x: event.clientX, y: event.clientY },
        ...(layout === null ? {} : { container: layout }),
        onClose: () => undefined,
        items: [
          { key: "beside", label: t("shell.tabs.moveBeside"), onSelect: () => { controller.moveBeside(id); } },
          { type: "divider", key: "divider" },
          { key: "close", label: t("shell.tabs.close"), onSelect: () => { controller.close(id); } },
          { key: "closeOthers", label: t("shell.tabs.closeOthers"), onSelect: () => { controller.closeOthers(id); } },
        ],
      });
    },
    [controller, t],
  );
  return (
    <>
    <Layout
      ref={(layout) => {
        const root = layout?.getRootDiv();
        if (root == null) return;
        const focusTab = (event: FocusEvent) => {
          // The layout's own tabs only: a tab inside a pane's panel is the page's (a result's Provenance).
          if (!(event.target instanceof HTMLElement) || !event.target.matches('[role="tab"]') || event.target.closest('[role="tabpanel"]') !== null) return;
          const focus = tooltip.focusProps(event.target.getAttribute("aria-label") ?? "");
          event.target.setAttribute("data-tooltip-owner", focus["data-tooltip-owner"]);
          focus.onFocus({ currentTarget: event.target });
        };
        const blurTab = tooltip.focusProps("").onBlur;
        root.addEventListener("focusin", focusTab);
        root.addEventListener("focusout", blurTab);
        return () => {
          root.removeEventListener("focusin", focusTab);
          root.removeEventListener("focusout", blurTab);
        };
      }}
      model={controller.model}
      factory={factory}
      onAction={controller.intercept}
      onContextMenu={showTabMenu}
      i18nTranslator={translate}
      icons={ICONS}
      onRenderTab={(node, values) => {
        const path = pathnameOf(tabPlace(node)?.route ?? "");
        const Icon = APP_SECTIONS.find(({ id }) => id === sectionOfPath(path))?.icon
          ?? PAGE_LABELS[path.split("/")[1] ?? ""]?.icon ?? DocumentIcon;
        const title = layoutString(node.getName(), t);
        values.leading = <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />;
        values.content = <span {...tooltip.hoverProps(title)}>{title}</span>;
      }}
      onRenderTabSet={(group, values) => {
        const from = group.getSelectedNode();
        if (!(from instanceof TabNode)) return;
        values.stickyButtons.push(
          <div key="new-tab" className="app-tab-chooser flex items-center">
            <IconButton label={t("shell.tabs.newTab")} onClick={() => { controller.open(from.getId(), ROUTE_PATTERNS.newTab, "newTab"); }}>
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
            </IconButton>
            <IconButton label={t("shell.tabs.choose")} aria-haspopup="menu" onClick={(event) => {
              const container = event.currentTarget.closest<HTMLElement>(".flexlayout__layout");
              showPopupMenu({
                anchor: event.currentTarget,
                returnFocusTo: event.currentTarget,
                ...(container === null ? {} : { container }),
                title: t("shell.tabs.choose"),
                onClose: () => undefined,
                items: [
                  { key: "thread", label: t("threads.prepare.run.newThread"), icon: <PlusIcon className="h-4 w-4" aria-hidden="true" />, onSelect: () => { controller.open(from.getId(), ROUTE_PATTERNS.threads, "newTab"); } },
                  ...APP_SECTIONS.filter(({ id }) => id !== "threads").map(({ id, path, labelKey, icon: Icon }) => ({
                    key: id, label: t(labelKey), icon: <Icon className="h-4 w-4" aria-hidden="true" />,
                    onSelect: () => { controller.open(from.getId(), path, "newTab"); },
                  })),
                  ...(recentlyClosed.length === 0 ? [] : [
                    { key: "divider", type: "divider" as const },
                    { key: "closed", label: t("shell.tabs.recentlyClosed"), disabled: true },
                  ]),
                  ...recentlyClosed.map((closed, index) => {
                    const path = pathnameOf(closed.route);
                    const Icon = APP_SECTIONS.find(({ id }) => id === sectionOfPath(path))?.icon
                      ?? PAGE_LABELS[path.split("/")[1] ?? ""]?.icon ?? DocumentIcon;
                    return {
                      key: `closed-${index}`, label: layoutString(closed.name, t), icon: <Icon className="h-4 w-4" aria-hidden="true" />,
                      onSelect: () => { controller.reopenClosed(index, from.getId()); },
                    };
                  }),
                ],
              });
            }}><ChevronDownIcon className="h-4 w-4" aria-hidden="true" /></IconButton>
          </div>,
        );
      }}
      invalidateTabContentOnParentRender={false}
    />
    {tooltip.shown === null ? null : <AnchoredTooltip anchor={tooltip.shown.anchor}>{tooltip.shown.content}</AnchoredTooltip>}
    </>
  );
}
